/**
 * Clinical AI Agent - Aide Supervisory Tab
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserCheck, Calendar, Eye, Loader2, CheckCircle, AlertTriangle, Clock
} from "lucide-react";
import { toast } from "sonner";
import { SUPERVISORY_VISIT_TYPES, API_URL, getSeverityClass } from "./constants";

export default function AideTab({
  patientId,
  loading,
  setLoading,
  isDark,
  token
}) {
  const [supervisoryForm, setSupervisoryForm] = useState({
    aide_id: "",
    aide_name: "",
    visit_type: "routine",
    care_plan_reviewed: false,
    competency_verified: false,
    clinical_notes: ""
  });
  const [supervisoryDue, setSupervisoryDue] = useState(null);
  const [supervisoryResult, setSupervisoryResult] = useState(null);

  // Fetch supervisory visits due
  const fetchSupervisoryDue = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/aide/supervisory-due?days_ahead=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSupervisoryDue(data);
      }
    } catch (error) {
      console.error("Supervisory due fetch error:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchSupervisoryDue();
  }, [fetchSupervisoryDue]);

  // Submit supervisory visit
  const submitSupervisoryVisit = async () => {
    if (!supervisoryForm.aide_id || !supervisoryForm.aide_name) {
      toast.error("Please enter aide information");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/clinical-ai-agent/aide/supervisory-visit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          ...supervisoryForm
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSupervisoryResult(data);
        toast.success("Supervisory visit documented");
        fetchSupervisoryDue();
      } else {
        toast.error("Failed to document visit");
      }
    } catch (error) {
      toast.error("Error submitting supervisory visit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Due Visits Summary */}
      {supervisoryDue && (
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-400" />
              Supervisory Visits Due
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className={`p-2 rounded text-center ${isDark ? "bg-red-900/20" : "bg-red-50"}`}>
                <p className="text-2xl font-bold text-red-400">{supervisoryDue.summary?.overdue || 0}</p>
                <p className="text-xs text-gray-500">Overdue</p>
              </div>
              <div className={`p-2 rounded text-center ${isDark ? "bg-yellow-900/20" : "bg-yellow-50"}`}>
                <p className="text-2xl font-bold text-yellow-400">{supervisoryDue.summary?.due_today || 0}</p>
                <p className="text-xs text-gray-500">Due Today</p>
              </div>
              <div className={`p-2 rounded text-center ${isDark ? "bg-blue-900/20" : "bg-blue-50"}`}>
                <p className="text-2xl font-bold text-blue-400">{supervisoryDue.summary?.upcoming || 0}</p>
                <p className="text-xs text-gray-500">Upcoming</p>
              </div>
            </div>

            {supervisoryDue.overdue_visits && supervisoryDue.overdue_visits.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-red-400">Overdue:</p>
                {supervisoryDue.overdue_visits.slice(0, 3).map((visit, i) => (
                  <div 
                    key={i}
                    className={`p-2 rounded flex items-center justify-between ${isDark ? "bg-slate-700" : "bg-gray-50"}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{visit.patient_name}</p>
                      <p className="text-xs text-gray-500">Aide: {visit.aide_name}</p>
                    </div>
                    <Badge className="bg-red-500/20 text-red-400">
                      {visit.days_overdue} days overdue
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Supervisory Visit Form */}
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-cyan-400" />
            Document Supervisory Visit
          </CardTitle>
          <CardDescription className="text-xs">
            CMS requires RN supervision of aides every 14 days minimum
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Aide ID</Label>
              <Input
                placeholder="Aide employee ID"
                value={supervisoryForm.aide_id}
                onChange={(e) => setSupervisoryForm({ ...supervisoryForm, aide_id: e.target.value })}
                className={isDark ? "bg-slate-700 border-slate-600" : ""}
                data-testid="aide-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Aide Name</Label>
              <Input
                placeholder="Aide name"
                value={supervisoryForm.aide_name}
                onChange={(e) => setSupervisoryForm({ ...supervisoryForm, aide_name: e.target.value })}
                className={isDark ? "bg-slate-700 border-slate-600" : ""}
                data-testid="aide-name-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Visit Type</Label>
            <Select
              value={supervisoryForm.visit_type}
              onValueChange={(v) => setSupervisoryForm({ ...supervisoryForm, visit_type: v })}
            >
              <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPERVISORY_VISIT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Care Plan Reviewed</Label>
              <Switch
                checked={supervisoryForm.care_plan_reviewed}
                onCheckedChange={(v) => setSupervisoryForm({ ...supervisoryForm, care_plan_reviewed: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Competency Verified</Label>
              <Switch
                checked={supervisoryForm.competency_verified}
                onCheckedChange={(v) => setSupervisoryForm({ ...supervisoryForm, competency_verified: v })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Clinical Notes</Label>
            <Textarea
              placeholder="Observations, findings, and recommendations..."
              value={supervisoryForm.clinical_notes}
              onChange={(e) => setSupervisoryForm({ ...supervisoryForm, clinical_notes: e.target.value })}
              className={isDark ? "bg-slate-700 border-slate-600" : ""}
              rows={3}
            />
          </div>

          <Button
            onClick={submitSupervisoryVisit}
            disabled={loading || !supervisoryForm.aide_id || !supervisoryForm.aide_name}
            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600"
            data-testid="submit-supervisory-visit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
            Document Supervisory Visit
          </Button>
        </CardContent>
      </Card>

      {/* Supervisory Result */}
      {supervisoryResult && (
        <Card className={`${isDark ? "bg-slate-800/80 border-cyan-500/30" : "bg-cyan-50 border-cyan-200"}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              Supervisory Visit Documented
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-64">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={getSeverityClass(
                    supervisoryResult.compliance_status === "compliant" ? "low" : 
                    supervisoryResult.compliance_status === "needs_attention" ? "moderate" : "high"
                  )}>
                    {supervisoryResult.compliance_status}
                  </Badge>
                  {supervisoryResult.days_since_last_visit !== undefined && (
                    <Badge variant="outline">
                      {supervisoryResult.days_since_last_visit} days since last visit
                    </Badge>
                  )}
                </div>

                {supervisoryResult.competency_assessment && (
                  <div>
                    <p className="text-xs font-semibold mb-1">Competency Assessment:</p>
                    <div className={`p-2 rounded ${isDark ? "bg-slate-700" : "bg-white"}`}>
                      <p className="text-xs">
                        <span className="font-medium">Satisfactory:</span>{" "}
                        {supervisoryResult.competency_assessment.satisfactory ? "Yes" : "No"}
                      </p>
                      {supervisoryResult.competency_assessment.areas_observed && (
                        <p className="text-xs mt-1">
                          <span className="font-medium">Areas Observed:</span>{" "}
                          {supervisoryResult.competency_assessment.areas_observed.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {supervisoryResult.care_plan_updates_needed && supervisoryResult.care_plan_updates_needed.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1 text-yellow-400">Care Plan Updates Needed:</p>
                    <ul className="text-xs space-y-1">
                      {supervisoryResult.care_plan_updates_needed.map((update, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <AlertTriangle className="h-3 w-3 text-yellow-400 mt-0.5" />
                          {update}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {supervisoryResult.documentation_template && (
                  <div className="p-2 rounded bg-gray-100 dark:bg-slate-700">
                    <p className="text-xs font-semibold mb-1">Documentation:</p>
                    <p className="text-xs italic">{supervisoryResult.documentation_template}</p>
                  </div>
                )}

                {supervisoryResult.next_visit_due && (
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="h-3 w-3 text-cyan-400" />
                    Next visit due: {new Date(supervisoryResult.next_visit_due).toLocaleDateString()}
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
